import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, Link, useLocation, useNavigate } from 'react-router-dom';
import api from '../api/client';
import MarkdownText from '../components/MarkdownText';
import StudentMessengerCell from '../components/StudentMessengerCell';
import TasksByTagProgressPanel from '../components/TasksByTagProgressPanel';
import ReadOnlyPythonCode from '../components/ReadOnlyPythonCode';
import Modal from '../components/Modal';
import { taskNavRef } from '../utils/taskNav';

const SELF_REPORT_LABELS = {
  EASY: '++',
  OK_NO_PEEK: '+',
  OK_WITH_PEEK: '+-',
  OK_WITH_QUESTION: '+?',
  FAILED: '-',
  NO_IDEAS: '--',
  NOT_TRIED: '!',
};

const VERDICT_LABELS = {
  ACCEPTED: 'Принять',
  REVIEW_IN_LESSON: 'Посмотрим на занятии',
  REDO: 'Переделать',
};

const DEBOUNCE_MS = 600;

/**
 * Страница работы преподавателя с работами ученика.
 * При необходимости можно перевести на тот же блоковый layout (WorkPageBlocksLayout),
 * что и ученическая страница, с дополнительными блоками управления списками и вердиктами.
 */
function fmtListDateTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

function CollapsibleLeftSection({ sectionId, title, open, onToggle, children }) {
  return (
    <section className={`teacher-work-collapsible ${open ? 'is-open' : 'is-collapsed'}`}>
      <button
        type="button"
        className="teacher-work-collapsible-head"
        aria-expanded={open}
        onClick={() => onToggle(sectionId)}
      >
        <span className="teacher-work-collapsible-chevron" aria-hidden>{open ? '▼' : '▶'}</span>
        <span className="teacher-work-collapsible-title">{title}</span>
      </button>
      {open ? <div className="teacher-work-collapsible-body">{children}</div> : null}
    </section>
  );
}

export default function TeacherStudentWorkPage() {
  const { studentId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [listsScope, setListsScope] = useState('mine'); // 'all' | 'mine' | 'other' | 'self'
  const [listsStatusFilter, setListsStatusFilter] = useState('all'); // 'all' | 'in_progress'
  const [leftSectionsOpen, setLeftSectionsOpen] = useState({
    lists: true,
    tasks: true,
    recent: true,
    byTag: true,
  });
  const [selectedListId, setSelectedListId] = useState(null);
  const [selectedTaskSlug, setSelectedTaskSlug] = useState(null);
  const [taskDetail, setTaskDetail] = useState(null);
  const [attempts, setAttempts] = useState([]);
  const [selectedAttemptId, setSelectedAttemptId] = useState(null);
  const [attemptDetail, setAttemptDetail] = useState(null);
  const [commentDraft, setCommentDraft] = useState('');
  const [commentSaved, setCommentSaved] = useState(false);
  const [savingComment, setSavingComment] = useState(false);
  const debounceRef = useRef(null);
  const [agendaSaving, setAgendaSaving] = useState(false);
  const [workTab, setWorkTab] = useState('student'); // 'student' | 'prep'
  const [prepTaskSearch, setPrepTaskSearch] = useState('');
  const [prepTaskResults, setPrepTaskResults] = useState([]);
  const [prepTaskSearching, setPrepTaskSearching] = useState(false);
  const [prepTaskSearchError, setPrepTaskSearchError] = useState('');
  const [prepTaskSearched, setPrepTaskSearched] = useState(false);
  const [topicPlan, setTopicPlan] = useState([]);
  const [topicPlanLoading, setTopicPlanLoading] = useState(false);
  const [topicPlanOpen, setTopicPlanOpen] = useState(false);
  const [listSaving, setListSaving] = useState(false);
  const [listAddOpen, setListAddOpen] = useState(false);
  const [listAddSearch, setListAddSearch] = useState('');
  const [listAddResults, setListAddResults] = useState([]);
  const [listAddSearching, setListAddSearching] = useState(false);
  const viewingIntervalRef = useRef(null);
  const overviewIntervalRef = useRef(null);
  const selectedListIdRef = useRef(null);
  const listsScopeRef = useRef('mine');
  const lessonNotesDebounceRef = useRef(null);
  const [lessonNotesDraft, setLessonNotesDraft] = useState('');
  const [lessonNotesSaved, setLessonNotesSaved] = useState(false);
  const [savingLessonNotes, setSavingLessonNotes] = useState(false);
  const [endingLesson, setEndingLesson] = useState(false);
  const [similarTasks, setSimilarTasks] = useState([]);
  const [issueDialogOpen, setIssueDialogOpen] = useState(false);
  const [issueSessionId, setIssueSessionId] = useState(null);
  const [issueTitle, setIssueTitle] = useState('');
  const [issueDeadline, setIssueDeadline] = useState('');
  const [issueSaving, setIssueSaving] = useState(false);
  const [issueAndEnd, setIssueAndEnd] = useState(false);
  const [endDialogOpen, setEndDialogOpen] = useState(false);

  const activeSession = overview?.active_lesson_session;
  const leftoverSession = overview?.leftover_homework_session;
  const lessonAgenda = activeSession?.lesson_agenda || [];
  const homeworkDraft = activeSession?.homework_draft || [];
  const leftoverDraft = leftoverSession?.homework_draft || [];

  const loadOverview = useCallback(async (opts = {}) => {
    const silent = opts.silent === true;
    if (!studentId) return;
    if (!silent) {
      setLoading(true);
      setError('');
    }
    try {
      const res = await api.get(`/teacher/students/${studentId}/overview/`);
      setOverview(res.data);
      const mine = Array.isArray(res.data?.task_lists) ? res.data.task_lists : [];
      const other = Array.isArray(res.data?.other_teacher_task_lists) ? res.data.other_teacher_task_lists : [];
      const self = Array.isArray(res.data?.student_self_task_lists) ? res.data.student_self_task_lists : [];
      const allIds = new Set([...mine, ...other, ...self].map((l) => l.id));
      const mergedSorted = [...mine, ...other, ...self].sort((a, b) => {
        const ta = a.issued_at ? new Date(a.issued_at).getTime() : 0;
        const tb = b.issued_at ? new Date(b.issued_at).getTime() : 0;
        return tb - ta;
      });
      const scope = listsScopeRef.current;
      const pool = scope === 'all' ? mergedSorted : scope === 'mine' ? mine : scope === 'other' ? other : self;
      const cur = selectedListIdRef.current;
      if (cur != null && allIds.has(cur)) {
        /* сохраняем выбор списка */
      } else if (pool.length > 0) {
        setSelectedListId(pool[0].id);
      } else {
        setSelectedListId(null);
      }
    } catch (e) {
      if (!silent) {
        const msg = e.response?.data?.detail || e.response?.data?.message || 'Не удалось загрузить данные ученика.';
        setError(typeof msg === 'string' ? msg : 'Не удалось загрузить данные ученика.');
        setOverview(null);
      } else {
        console.error('Teacher overview silent refresh failed', e);
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [studentId]);

  useEffect(() => {
    loadOverview();
  }, [studentId]);

  useEffect(() => {
    if (!studentId) return;
    const postViewing = () => api.post(`/teacher/students/${studentId}/viewing/`).catch(() => {});
    postViewing();
    viewingIntervalRef.current = setInterval(postViewing, 25000);
    return () => {
      if (viewingIntervalRef.current) clearInterval(viewingIntervalRef.current);
    };
  }, [studentId]);

  useEffect(() => {
    selectedListIdRef.current = selectedListId;
  }, [selectedListId]);

  useEffect(() => {
    listsScopeRef.current = listsScope;
  }, [listsScope]);

  useEffect(() => {
    const slug = location.state?.selectTaskSlug;
    if (!slug) return;
    setSelectedTaskSlug(slug);
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state, location.pathname, navigate]);

  useEffect(() => {
    if (!studentId) return;
    const id = setInterval(() => {
      loadOverview({ silent: true });
    }, 30000);
    overviewIntervalRef.current = id;
    return () => {
      if (id) clearInterval(id);
    };
  }, [studentId, loadOverview]);

  const myLists = useMemo(
    () => (Array.isArray(overview?.task_lists) ? overview.task_lists : []),
    [overview?.task_lists],
  );
  const otherLists = useMemo(
    () => (Array.isArray(overview?.other_teacher_task_lists) ? overview.other_teacher_task_lists : []),
    [overview?.other_teacher_task_lists],
  );
  const selfLists = useMemo(
    () => (Array.isArray(overview?.student_self_task_lists) ? overview.student_self_task_lists : []),
    [overview?.student_self_task_lists],
  );

  const mergedSortedLists = useMemo(() => {
    const merged = [...myLists, ...otherLists, ...selfLists];
    merged.sort((a, b) => {
      const ta = a.issued_at ? new Date(a.issued_at).getTime() : 0;
      const tb = b.issued_at ? new Date(b.issued_at).getTime() : 0;
      return tb - ta;
    });
    return merged;
  }, [myLists, otherLists, selfLists]);

  const rawListsForScope = useMemo(() => {
    if (listsScope === 'all') return mergedSortedLists;
    if (listsScope === 'mine') return myLists;
    if (listsScope === 'other') return otherLists;
    return selfLists;
  }, [listsScope, mergedSortedLists, myLists, otherLists, selfLists]);

  const activeLists = useMemo(() => {
    if (listsStatusFilter === 'in_progress') {
      return rawListsForScope.filter((l) => l.student_status === 'IN_PROGRESS');
    }
    return rawListsForScope;
  }, [rawListsForScope, listsStatusFilter]);

  useEffect(() => {
    if (selectedListId == null) return;
    if (!activeLists.some((l) => l.id === selectedListId)) {
      setSelectedListId(activeLists[0]?.id ?? null);
    }
  }, [activeLists, selectedListId]);

  const selectedList = activeLists.find((l) => l.id === selectedListId)
    || myLists.find((l) => l.id === selectedListId)
    || otherLists.find((l) => l.id === selectedListId)
    || selfLists.find((l) => l.id === selectedListId);
  const tasksInList = selectedList?.items || [];
  const canOpenSelectedList = Boolean(Array.isArray(tasksInList) && tasksInList.length > 0);
  const firstItem = canOpenSelectedList ? tasksInList[0] : null;
  const firstTaskSlug = firstItem?.task ? taskNavRef(firstItem.task) : null;
  const hasActiveLesson = Boolean(activeSession?.id);
  const selectedListWorkUrl = firstTaskSlug && selectedList?.id
    ? (hasActiveLesson
      ? `/sessions/${activeSession.id}/tasks/${firstTaskSlug}?list=${selectedList.id}`
      : `/work/${firstTaskSlug}?list=${selectedList.id}`)
    : null;

  useEffect(() => {
    if (!selectedTaskSlug || !studentId) {
      setTaskDetail(null);
      setAttempts([]);
      setAttemptDetail(null);
      setSelectedAttemptId(null);
      setCommentDraft('');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const [taskRes, attemptsRes] = await Promise.all([
          api.get(`/tasks/${selectedTaskSlug}/`),
          api.get(`/teacher/students/${studentId}/attempts/`, { params: { task: selectedTaskSlug } }),
        ]);
        if (cancelled) return;
        setTaskDetail(taskRes.data);
        const list = Array.isArray(attemptsRes.data) ? attemptsRes.data : attemptsRes.data?.results || [];
        setAttempts(list);
        const latest = list[0];
        if (latest) {
          setSelectedAttemptId(latest.id);
          setAttemptDetail(latest);
          setCommentDraft(latest.teacher_comment || '');
        } else {
          setSelectedAttemptId(null);
          setAttemptDetail(null);
          setCommentDraft('');
        }
      } catch {
        if (!cancelled) {
          setTaskDetail(null);
          setAttempts([]);
          setAttemptDetail(null);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [studentId, selectedTaskSlug]);

  useEffect(() => {
    if (!selectedTaskSlug) {
      setSimilarTasks([]);
      return undefined;
    }
    let cancelled = false;
    api
      .get(`/tasks/${selectedTaskSlug}/similar/`)
      .then((res) => {
        if (!cancelled) {
          setSimilarTasks(Array.isArray(res.data) ? res.data : res.data?.results ?? []);
        }
      })
      .catch(() => {
        if (!cancelled) setSimilarTasks([]);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedTaskSlug]);

  useEffect(() => {
    if (!attemptDetail) return;
    setCommentDraft(attemptDetail.teacher_comment || '');
  }, [attemptDetail?.id]);

  const saveComment = useCallback(async (text) => {
    if (!studentId || !selectedAttemptId) return;
    setSavingComment(true);
    try {
      await api.patch(`/teacher/students/${studentId}/attempts/${selectedAttemptId}/`, {
        teacher_comment: text,
      });
      setCommentSaved(true);
      setAttemptDetail((prev) => (prev ? { ...prev, teacher_comment: text } : null));
      setTimeout(() => setCommentSaved(false), 2000);
    } catch {
      // keep draft
    } finally {
      setSavingComment(false);
    }
  }, [studentId, selectedAttemptId]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!commentDraft.trim() && !attemptDetail?.teacher_comment) return;
    debounceRef.current = setTimeout(() => {
      saveComment(commentDraft);
    }, DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [commentDraft, saveComment]);

  const activeLessonSessionId = overview?.active_lesson_session?.id ?? null;

  useEffect(() => {
    if (!activeLessonSessionId) {
      setLessonNotesDraft('');
      return;
    }
    setLessonNotesDraft(overview?.active_lesson_session?.teacher_notes || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps -- только при смене сессии, не затирать черновик при silent refresh
  }, [activeLessonSessionId]);

  const saveLessonNotes = useCallback(async (text) => {
    const sessionId = overview?.active_lesson_session?.id;
    if (!sessionId) return;
    setSavingLessonNotes(true);
    try {
      await api.patch(`/teacher/lesson-sessions/${sessionId}/`, { teacher_notes: text });
      setLessonNotesSaved(true);
      setOverview((prev) => {
        if (!prev?.active_lesson_session) return prev;
        return {
          ...prev,
          active_lesson_session: { ...prev.active_lesson_session, teacher_notes: text },
        };
      });
      setTimeout(() => setLessonNotesSaved(false), 2000);
    } catch {
      /* оставляем черновик */
    } finally {
      setSavingLessonNotes(false);
    }
  }, [overview?.active_lesson_session?.id]);

  useEffect(() => {
    if (lessonNotesDebounceRef.current) clearTimeout(lessonNotesDebounceRef.current);
    const sessionId = overview?.active_lesson_session?.id;
    if (!sessionId) return undefined;
    const server = overview?.active_lesson_session?.teacher_notes ?? '';
    if (lessonNotesDraft === server) return undefined;
    lessonNotesDebounceRef.current = setTimeout(() => {
      saveLessonNotes(lessonNotesDraft);
    }, DEBOUNCE_MS);
    return () => {
      if (lessonNotesDebounceRef.current) clearTimeout(lessonNotesDebounceRef.current);
    };
  }, [
    lessonNotesDraft,
    overview?.active_lesson_session?.id,
    overview?.active_lesson_session?.teacher_notes,
    saveLessonNotes,
  ]);

  const setVerdict = async (verdict) => {
    if (!studentId || !selectedAttemptId) return;
    try {
      await api.patch(`/teacher/students/${studentId}/attempts/${selectedAttemptId}/`, {
        teacher_verdict: verdict,
      });
      setAttemptDetail((prev) => (prev ? { ...prev, teacher_verdict: verdict } : null));
    } catch (e) {
      console.error(e);
    }
  };

  async function loadTopicPlan() {
    if (!studentId) return;
    setTopicPlanLoading(true);
    try {
      const res = await api.get(`/teacher/students/${studentId}/topic-plan/`);
      setTopicPlan(Array.isArray(res.data) ? res.data : []);
    } catch {
      setTopicPlan([]);
    } finally {
      setTopicPlanLoading(false);
    }
  }

  async function searchPrepTasks() {
    if (!prepTaskSearch.trim()) return;
    setPrepTaskSearching(true);
    setPrepTaskSearchError('');
    setPrepTaskSearched(true);
    try {
      const res = await api.get('/teacher/tasks-for-list/', { params: { search: prepTaskSearch.trim() } });
      setPrepTaskResults(Array.isArray(res.data) ? res.data : res.data?.results ?? []);
    } catch (e) {
      const msg = e.response?.data?.detail || 'Не удалось выполнить поиск.';
      setPrepTaskSearchError(typeof msg === 'string' ? msg : 'Не удалось выполнить поиск.');
      setPrepTaskResults([]);
    } finally {
      setPrepTaskSearching(false);
    }
  }

  const addToAgenda = async (type, taskId) => {
    if (!activeSession?.id) return;
    const raw = type === 'lesson' ? (activeSession.lesson_agenda || []) : (activeSession.homework_draft || []);
    if (raw.some((x) => x.task_id === taskId)) return;
    const list = [
      ...raw.map((x, i) => ({ task_id: x.task_id, position: i })),
      { task_id: taskId, position: raw.length },
    ];
    setAgendaSaving(true);
    try {
      const res = await api.patch(`/teacher/lesson-sessions/${activeSession.id}/`, type === 'lesson' ? { lesson_agenda: list } : { homework_draft: list });
      setOverview((prev) => {
        if (!prev) return prev;
        const session = { ...prev.active_lesson_session, ...res.data };
        return { ...prev, active_lesson_session: session };
      });
    } finally {
      setAgendaSaving(false);
    }
  };

  const removeFromAgenda = async (type, taskId) => {
    if (!activeSession?.id) return;
    const list = (type === 'lesson' ? [...(activeSession.lesson_agenda || [])] : [...(activeSession.homework_draft || [])])
      .filter((x) => x.task_id !== taskId)
      .map((x, i) => ({ task_id: x.task_id, position: i }));
    setAgendaSaving(true);
    try {
      const res = await api.patch(`/teacher/lesson-sessions/${activeSession.id}/`, type === 'lesson' ? { lesson_agenda: list } : { homework_draft: list });
      setOverview((prev) => {
        if (!prev) return prev;
        const session = { ...prev.active_lesson_session, ...res.data };
        return { ...prev, active_lesson_session: session };
      });
    } finally {
      setAgendaSaving(false);
    }
  };

  function defaultHomeworkTitle() {
    const now = new Date();
    const dd = String(now.getDate()).padStart(2, '0');
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    return `ДЗ ${dd}.${mm}`;
  }

  function openIssueDialog(sessionId, { andEnd = false } = {}) {
    setIssueSessionId(sessionId);
    setIssueTitle(defaultHomeworkTitle());
    setIssueDeadline('');
    setIssueAndEnd(andEnd);
    setIssueDialogOpen(true);
  }

  async function submitIssueHomework() {
    if (!issueSessionId) return;
    setIssueSaving(true);
    try {
      const payload = { title: issueTitle.trim() || defaultHomeworkTitle() };
      if (issueDeadline) {
        payload.deadline_at = `${issueDeadline}T23:59:59`;
      }
      await api.post(`/teacher/lesson-sessions/${issueSessionId}/issue-homework/`, payload);
      if (issueAndEnd) {
        await api.post(`/tasks/lesson-sessions/${issueSessionId}/end/`);
      }
      setIssueDialogOpen(false);
      setEndDialogOpen(false);
      setIssueSessionId(null);
      setIssueAndEnd(false);
      await loadOverview();
    } catch (e) {
      const msg = e.response?.data?.detail || 'Не удалось выдать ДЗ.';
      alert(typeof msg === 'string' ? msg : 'Не удалось выдать ДЗ.');
    } finally {
      setIssueSaving(false);
    }
  }

  async function endLessonWithoutIssue() {
    if (!activeSession?.id) return;
    setEndingLesson(true);
    try {
      await api.post(`/tasks/lesson-sessions/${activeSession.id}/end/`);
      setEndDialogOpen(false);
      await loadOverview();
    } catch (e) {
      const msg = e.response?.data?.detail || 'Не удалось завершить урок.';
      alert(typeof msg === 'string' ? msg : 'Не удалось завершить урок.');
    } finally {
      setEndingLesson(false);
    }
  }

  async function clearLeftoverDraft() {
    if (!leftoverSession?.id) return;
    if (!window.confirm('Очистить невыданный черновик ДЗ?')) return;
    setAgendaSaving(true);
    try {
      await api.patch(`/teacher/lesson-sessions/${leftoverSession.id}/`, { homework_draft: [] });
      await loadOverview();
    } catch (e) {
      const msg = e.response?.data?.detail || 'Не удалось очистить черновик.';
      alert(typeof msg === 'string' ? msg : 'Не удалось очистить черновик.');
    } finally {
      setAgendaSaving(false);
    }
  }

  function requestEndLesson() {
    if (!activeSession?.id) return;
    if ((activeSession.homework_draft || []).length > 0) {
      setEndDialogOpen(true);
      return;
    }
    if (!window.confirm('Завершить урок? После этого можно начать новый.')) return;
    void endLessonWithoutIssue();
  }

  function buildTaskListItemsPayload(items) {
    return (items || []).map((it, i) => ({
      task_id: it.task?.id ?? it.task_id,
      position: i,
      teacher_note: it.teacher_note ?? '',
    }));
  }

  const removeFromTaskList = async (taskId) => {
    if (!selectedListId || !selectedList?.items) return;
    const newItems = selectedList.items
      .filter((it) => (it.task?.id ?? it.task_id) !== taskId)
      .map((it, i) => ({ ...it, position: i }));
    const payload = { items: buildTaskListItemsPayload(newItems) };
    setListSaving(true);
    try {
      await api.patch(`/teacher/task-lists/${selectedListId}/`, payload);
      await loadOverview();
    } catch (e) {
      console.error('Remove from task list failed', e);
    } finally {
      setListSaving(false);
    }
  };

  const addToTaskList = async (taskId) => {
    if (!selectedListId || !selectedList?.items) return;
    const existingIds = selectedList.items.map((it) => it.task?.id ?? it.task_id);
    if (existingIds.includes(taskId)) return;
    const newItems = [
      ...selectedList.items,
      { task_id: taskId, position: selectedList.items.length, teacher_note: '' },
    ];
    const payload = { items: buildTaskListItemsPayload(newItems) };
    setListSaving(true);
    try {
      await api.patch(`/teacher/task-lists/${selectedListId}/`, payload);
      await loadOverview();
      setListAddResults((prev) => prev.filter((t) => t.id !== taskId));
    } catch (e) {
      console.error('Add to task list failed', e);
    } finally {
      setListSaving(false);
    }
  };

  async function searchListAddTasks() {
    if (!listAddSearch.trim()) {
      setListAddResults([]);
      return;
    }
    setListAddSearching(true);
    try {
      const res = await api.get('/teacher/tasks-for-list/', { params: { search: listAddSearch.trim() } });
      setListAddResults(Array.isArray(res.data) ? res.data : res.data?.results ?? []);
    } catch {
      setListAddResults([]);
    } finally {
      setListAddSearching(false);
    }
  }

  const currentTaskId = taskDetail?.id;

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <p>Загрузка...</p>
      </div>
    );
  }

  if (error || !overview) {
    return (
      <div className="teacher-student-work-page error-state">
        <p>{error || 'Ученик не найден.'}</p>
        <Link to="/teacher" className="btn btn-outline">В кабинет преподавателя</Link>
      </div>
    );
  }

  const studentName = [overview.student.last_name, overview.student.first_name].filter(Boolean).join(' ') || overview.student.username;
  const relationActive = overview.relation_status === 'active';

  return (
    <div className="teacher-student-work-page">
      {!relationActive && (
        <div className="alert alert-warning" role="alert">
          Связь не активна ({overview.relation_status || 'ожидает подтверждения'}). Кнопка «Начать урок» и добавление задач в списки занятия/ДЗ доступны только при активной связи.
        </div>
      )}
      <header className="teacher-student-work-header">
        <Link to="/teacher" className="back-link">← Кабинет</Link>
        <h1>{studentName}</h1>
        <span className="text-secondary">@{overview.student.username}</span>
        <StudentMessengerCell
          studentId={studentId}
          messenger={overview.messenger}
          onUpdated={(messenger) => {
            setOverview((prev) => (prev ? { ...prev, messenger } : prev));
          }}
        />
        {overview.is_online != null && (
          <span className={`presence-dot ${overview.is_online ? 'online' : 'offline'}`} title={overview.is_online ? 'Ученик в сети' : 'Не в сети'} />
        )}
        {overview.current_task_slug && (
          <span className="text-secondary current-task-hint">Задача: {overview.current_task_slug}</span>
        )}
        {overview.work_join_url ? (
          <Link to={overview.work_join_url} className="btn btn-sm btn-primary">
            Подключиться к работе
          </Link>
        ) : (
          <span
            className="text-secondary work-join-unavailable"
            style={{ fontSize: '0.9rem', maxWidth: '22rem' }}
            title="Нужна страница работы с параметром list и активная совместная сессия редактора"
          >
            Подключение недоступно: пусть ученик откроет задачу из списка урока (баннер «Урок идёт» или «Мои списки»).
          </span>
        )}
        <Link
          to={`/teacher/students/${studentId}/topic-progress`}
          className="btn btn-sm btn-outline"
        >
          Прогресс по темам
        </Link>
        <Link
          to={`/teacher/students/${studentId}/questionnaire`}
          className="btn btn-sm btn-outline"
        >
          {overview.student.has_questionnaire ? 'Анкета' : 'Анкета (нет)'}
        </Link>
        <Link
          to={`/teacher/students/${studentId}/activity`}
          className="btn btn-sm btn-outline"
        >
          Журнал активности
        </Link>
        {activeSession ? (
          <>
            <span className="badge session-badge">
              Урок #{activeSession.id}
            </span>
            {activeSession.work_list_id ? (
              <Link
                to={
                  activeSession.work_list_first_task_slug
                    ? `/work/${activeSession.work_list_first_task_slug}?list=${activeSession.work_list_id}`
                    : `/tasks`
                }
                className="btn btn-sm btn-outline"
                title={activeSession.work_list_title || 'Список урока'}
              >
                {activeSession.work_list_first_task_slug
                  ? `Список урока: ${activeSession.work_list_title || 'открыть'}`
                  : `${activeSession.work_list_title || 'Список урока'} (пока пуст)`}
              </Link>
            ) : null}
            <button
              type="button"
              className="btn btn-sm btn-outline"
              disabled={endingLesson}
              title="Завершить текущую сессию урока"
              onClick={requestEndLesson}
            >
              {endingLesson ? '…' : 'Завершить урок'}
            </button>
          </>
        ) : (
          <button
            type="button"
            className="btn btn-sm btn-primary"
            disabled={!relationActive}
            title={!relationActive ? 'Связь с учеником должна быть активной' : ''}
            onClick={async () => {
              if (!relationActive) return;
              try {
                await api.post('/teacher/lesson-sessions/', { student_id: Number(studentId) });
                await loadOverview();
              } catch (e) {
                const msg = e.response?.data?.detail || 'Не удалось начать урок.';
                alert(typeof msg === 'string' ? msg : 'Не удалось начать урок.');
              }
            }}
          >
            Начать урок
          </button>
        )}
      </header>

      <div className="teacher-work-tabs" role="tablist" aria-label="Режим работы">
        <button
          type="button"
          role="tab"
          aria-selected={workTab === 'student'}
          className={`tab-btn ${workTab === 'student' ? 'active' : ''}`}
          onClick={() => setWorkTab('student')}
        >
          Работа с учеником
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={workTab === 'prep'}
          className={`tab-btn ${workTab === 'prep' ? 'active' : ''}`}
          onClick={() => setWorkTab('prep')}
        >
          Подготовка (занятие / ДЗ)
        </button>
      </div>

      {/* При необходимости можно перевести на WorkPageBlocksLayout с блоками:
          текст задачи, список задач (с управлением), код попытки, комментарий, вердикт, блоки управления списками. */}
      <div className="teacher-student-work-layout">
        <aside className="teacher-work-left">
          <CollapsibleLeftSection
            sectionId="lists"
            title="Списки задач"
            open={leftSectionsOpen.lists}
            onToggle={(id) => setLeftSectionsOpen((o) => ({ ...o, [id]: !o[id] }))}
          >
            <div className="teacher-list-scope-row">
              <label className="text-secondary teacher-list-scope-label" htmlFor="teacher-list-scope-select">Источник</label>
              <select
                id="teacher-list-scope-select"
                className="form-select teacher-list-scope-select"
                value={listsScope}
                onChange={(e) => {
                  const v = e.target.value;
                  setListsScope(v);
                  const merged = [...myLists, ...otherLists, ...selfLists].sort((a, b) => {
                    const ta = a.issued_at ? new Date(a.issued_at).getTime() : 0;
                    const tb = b.issued_at ? new Date(b.issued_at).getTime() : 0;
                    return tb - ta;
                  });
                  const pool = v === 'all' ? merged : v === 'mine' ? myLists : v === 'other' ? otherLists : selfLists;
                  const filtered = listsStatusFilter === 'in_progress'
                    ? pool.filter((l) => l.student_status === 'IN_PROGRESS')
                    : pool;
                  const next = filtered[0] ?? pool[0] ?? null;
                  setSelectedListId(next?.id ?? null);
                }}
              >
                <option value="all">Все</option>
                <option value="mine">Выдано мной ({myLists.length})</option>
                <option value="other">Другие ({otherLists.length})</option>
                <option value="self">Самостоятельно ({selfLists.length})</option>
              </select>
              <div className="teacher-list-status-toggle" role="group" aria-label="Статус списков">
                <button
                  type="button"
                  className={`btn btn-xs ${listsStatusFilter === 'all' ? 'btn-primary' : 'btn-outline'}`}
                  onClick={() => setListsStatusFilter('all')}
                >
                  Все
                </button>
                <button
                  type="button"
                  className={`btn btn-xs ${listsStatusFilter === 'in_progress' ? 'btn-primary' : 'btn-outline'}`}
                  onClick={() => setListsStatusFilter('in_progress')}
                >
                  В работе
                </button>
              </div>
            </div>

            {activeLists.length === 0 ? (
              <p className="text-secondary">Нет списков в этой категории.</p>
            ) : (
              <ul className="task-list-tabs">
                {activeLists.map((lst) => (
                  <li key={lst.id}>
                    <button
                      type="button"
                      className={`task-list-tab-row ${selectedListId === lst.id ? 'active' : ''}`}
                      onClick={() => setSelectedListId(lst.id)}
                    >
                      <span className="task-list-tab-main">
                        <span className="task-list-tab-title">{lst.title}</span>
                        <span className="task-list-tab-dates text-secondary">
                          Выдано: {fmtListDateTime(lst.issued_at)}
                          {' · '}
                          Дедлайн: {fmtListDateTime(lst.deadline_at)}
                        </span>
                      </span>
                      <span className="list-status">
                        {lst.student_status === 'DONE' ? '✓' : '…'}
                        {lst.student_finished_at ? ' Сдано' : ''}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </CollapsibleLeftSection>

          {selectedList && (
            <CollapsibleLeftSection
              sectionId="tasks"
              title="Задачи в списке"
              open={leftSectionsOpen.tasks}
              onToggle={(id) => setLeftSectionsOpen((o) => ({ ...o, [id]: !o[id] }))}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem', alignItems: 'baseline', flexWrap: 'wrap' }}>
                {selectedListWorkUrl && (
                  <Link to={selectedListWorkUrl} className="btn btn-xs btn-outline">
                    Открыть в работе
                  </Link>
                )}
              </div>
              <ul className="task-list-items task-list-items-editable">
                {tasksInList.map((item) => {
                  const t = item.task;
                  const slug = t?.slug;
                  const taskId = t?.id ?? item.task_id;
                  const report = item.latest_attempt?.self_report_status ?? item.latest_self_report?.self_report_status;
                  const label = report ? SELF_REPORT_LABELS[report] || report : '—';
                  const statusClass = report === 'EASY' || report === 'OK_NO_PEEK' || report === 'OK_WITH_PEEK' || report === 'OK_WITH_QUESTION'
                    ? 'report-positive'
                    : report === 'FAILED' || report === 'NO_IDEAS'
                    ? 'report-negative'
                    : 'report-neutral';
                  return (
                    <li key={item.id} className={selectedTaskSlug === slug ? 'current' : ''}>
                      <button
                        type="button"
                        className="task-list-item-btn"
                        onClick={() => setSelectedTaskSlug(slug)}
                      >
                        <span className="task-title">{t?.title || slug || `#${item.id}`}</span>
                        <span className={`report-badge ${statusClass}`}>{label}</span>
                      </button>
                      <button
                        type="button"
                        className="btn btn-xs btn-outline task-list-item-remove"
                        title="Удалить из списка"
                        disabled={listSaving}
                        onClick={() => removeFromTaskList(taskId)}
                      >
                        ✕
                      </button>
                    </li>
                  );
                })}
              </ul>
              <div className="task-list-add-block">
                <button
                  type="button"
                  className="btn btn-outline btn-sm"
                  onClick={() => setListAddOpen((o) => !o)}
                >
                  {listAddOpen ? 'Свернуть' : '+ Добавить задачу в список'}
                </button>
                {listAddOpen && (
                  <div className="task-list-add-inline">
                    <div className="prep-search-row">
                      <input
                        type="text"
                        className="form-input"
                        placeholder="Поиск по названию или slug..."
                        value={listAddSearch}
                        onChange={(e) => setListAddSearch(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && searchListAddTasks()}
                      />
                      <button type="button" className="btn btn-outline" onClick={searchListAddTasks} disabled={listAddSearching}>
                        {listAddSearching ? 'Поиск…' : 'Найти'}
                      </button>
                    </div>
                    {listAddResults.length > 0 && (
                      <ul className="prep-task-results list-add-results">
                        {listAddResults.slice(0, 15).map((t) => {
                          const inList = tasksInList.some((it) => (it.task?.id ?? it.task_id) === t.id);
                          return (
                            <li key={t.id}>
                              <span>{t.title || t.slug}</span>
                              <button
                                type="button"
                                className="btn btn-xs btn-outline"
                                disabled={listSaving || inList}
                                onClick={() => addToTaskList(t.id)}
                              >
                                {inList ? 'В списке' : 'Добавить'}
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            </CollapsibleLeftSection>
          )}

          <CollapsibleLeftSection
            sectionId="recent"
            title="Последние попытки"
            open={leftSectionsOpen.recent}
            onToggle={(id) => setLeftSectionsOpen((o) => ({ ...o, [id]: !o[id] }))}
          >
            {overview.recent_attempts.length === 0 ? (
              <p className="text-secondary">Нет попыток</p>
            ) : (
              <ul className="recent-attempts-list">
                {overview.recent_attempts.slice(0, 15).map((a) => (
                  <li key={a.id}>
                    <button
                      type="button"
                      className="link-like"
                      onClick={() => setSelectedTaskSlug(a.task_slug)}
                    >
                      {a.task_title || a.task_slug} <span className="badge-small">{SELF_REPORT_LABELS[a.self_report_status] || a.self_report_status || '—'}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </CollapsibleLeftSection>

          <CollapsibleLeftSection
            sectionId="byTag"
            title="Задачи по тегу"
            open={leftSectionsOpen.byTag}
            onToggle={(id) => setLeftSectionsOpen((o) => ({ ...o, [id]: !o[id] }))}
          >
            <TasksByTagProgressPanel studentId={studentId} suppressBuiltInTitle />
          </CollapsibleLeftSection>
        </aside>

        <main className="teacher-work-main">
          {workTab === 'prep' ? (
            <div className="teacher-prep-tab">
              <h3>Добавить задачу в список занятия или ДЗ</h3>
              <p className="text-secondary">Найдите задачу и добавьте в «Задачи для занятия» или «ДЗ (черновик)» справа.</p>
              <div className="prep-search-row">
                <input
                  type="text"
                  className="form-input"
                  placeholder="Название, slug (ege16-199), номер в сборнике…"
                  value={prepTaskSearch}
                  onChange={(e) => setPrepTaskSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && searchPrepTasks()}
                />
                <button type="button" className="btn btn-outline" onClick={searchPrepTasks} disabled={prepTaskSearching}>
                  {prepTaskSearching ? 'Поиск…' : 'Найти'}
                </button>
              </div>
              {prepTaskSearchError && <div className="alert alert-error">{prepTaskSearchError}</div>}
              {prepTaskResults.length > 0 && (
                <ul className="prep-task-results">
                  {prepTaskResults.slice(0, 15).map((t) => (
                    <li key={t.id}>
                      <span>
                        {t.title || t.slug}
                        {t.slug ? <span className="text-secondary"> · {t.slug}</span> : null}
                      </span>
                      {activeSession && (
                        <>
                          <button type="button" className="btn btn-xs btn-outline" onClick={() => addToAgenda('lesson', t.id)}>В занятие</button>
                          <button type="button" className="btn btn-xs btn-outline" onClick={() => addToAgenda('homework', t.id)}>В ДЗ</button>
                        </>
                      )}
                    </li>
                  ))}
                </ul>
              )}
              {prepTaskSearched && !prepTaskSearching && !prepTaskSearchError && prepTaskResults.length === 0 && (
                <p className="text-secondary">Ничего не найдено. Попробуйте slug (например ege16-199) или часть названия.</p>
              )}
              {!activeSession && (
                <p className="text-secondary">Начните урок, чтобы добавлять задачи в списки.</p>
              )}
            </div>
          ) : !selectedTaskSlug ? (
            <div className="empty-selection">
              <p className="text-secondary">Выберите задачу слева или из последних попыток.</p>
            </div>
          ) : (
            <>
              <div className="task-statement-block">
                <h2>{taskDetail?.title || selectedTaskSlug}</h2>
                <MarkdownText className="statement-text" text={taskDetail?.statement || 'Загрузка...'} />
              </div>

              {attempts.length > 0 && (
                <div className="attempt-selector">
                  <label>Попытка:</label>
                  <select
                    value={selectedAttemptId || ''}
                    onChange={(e) => {
                      const id = Number(e.target.value);
                      setSelectedAttemptId(id);
                      const a = attempts.find((x) => x.id === id);
                      setAttemptDetail(a || null);
                    }}
                  >
                    {attempts.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.created_at ? new Date(a.created_at).toLocaleString('ru-RU') : a.id} — {SELF_REPORT_LABELS[a.self_report_status] || '—'}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {attemptDetail && (
                <>
                  <div className="attempt-code-block">
                    <h3>Код решения</h3>
                    {attemptDetail.code_snapshot?.trim()
                      ? <ReadOnlyPythonCode value={attemptDetail.code_snapshot} />
                      : <p className="text-secondary code-snapshot-empty">(пусто)</p>}
                  </div>
                  {attemptDetail.self_comment && (
                    <div className="self-comment-block">
                      <strong>Комментарий ученика:</strong>
                      <p>{attemptDetail.self_comment}</p>
                    </div>
                  )}
                  <div className="teacher-comment-block">
                    <label>Комментарий преподавателя</label>
                    <textarea
                      className="form-input"
                      rows={3}
                      value={commentDraft}
                      onChange={(e) => setCommentDraft(e.target.value)}
                      placeholder="Сохраняется автоматически"
                    />
                    <span className="save-indicator">
                      {savingComment ? 'Сохранение…' : commentSaved ? '✓ Сохранено' : ''}
                    </span>
                  </div>
                  <div className="verdict-and-similar">
                    <div className="verdict-buttons">
                      {(['ACCEPTED', 'REVIEW_IN_LESSON', 'REDO']).map((v) => (
                        <button
                          key={v}
                          type="button"
                          className={`btn btn-sm ${attemptDetail.teacher_verdict === v ? 'btn-primary' : 'btn-outline'}`}
                          onClick={() => setVerdict(v)}
                        >
                          {VERDICT_LABELS[v]}
                        </button>
                      ))}
                    </div>
                    {similarTasks.length > 0 && (
                      <div className="similar-tasks-near-verdict">
                        <div className="text-secondary similar-tasks-near-verdict-label">Аналоги</div>
                        <ul className="similar-tasks-near-verdict-list">
                          {similarTasks.map((st) => (
                            <li key={st.id}>
                              <Link to={`/work/${taskNavRef(st)}`} className="link-like similar-task-title">
                                {st.title || taskNavRef(st)}
                              </Link>
                              {activeSession ? (
                                <span className="similar-task-agenda-btns">
                                  <button
                                    type="button"
                                    className="btn btn-xs btn-outline"
                                    disabled={agendaSaving}
                                    onClick={() => addToAgenda('lesson', st.id)}
                                  >
                                    В занятие
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-xs btn-outline"
                                    disabled={agendaSaving}
                                    onClick={() => addToAgenda('homework', st.id)}
                                  >
                                    В ДЗ
                                  </button>
                                </span>
                              ) : (
                                <span className="text-secondary similar-task-no-session" title="Начните урок">
                                  Начните урок
                                </span>
                              )}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                  <div className="add-to-lists">
                    {activeSession && currentTaskId && (
                      <>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline"
                          disabled={agendaSaving}
                          onClick={() => addToAgenda('lesson', currentTaskId)}
                        >
                          Разберём на занятии
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline"
                          disabled={agendaSaving}
                          onClick={() => addToAgenda('homework', currentTaskId)}
                        >
                          В новую ДЗ
                        </button>
                      </>
                    )}
                  </div>
                </>
              )}
            </>
          )}
        </main>

        <aside className="teacher-work-right">
          {activeSession && (
            <div className="agenda-block lesson-notes-block">
              <h4>Заметки по уроку</h4>
              <textarea
                className="form-input"
                rows={5}
                value={lessonNotesDraft}
                onChange={(e) => setLessonNotesDraft(e.target.value)}
                placeholder="Краткий отчёт или планы — сохраняется автоматически"
                style={{ width: '100%', resize: 'vertical' }}
              />
              <span className="save-indicator">
                {savingLessonNotes ? 'Сохранение…' : lessonNotesSaved ? '✓ Сохранено' : ''}
              </span>
            </div>
          )}
          <div className="agenda-block">
            <h4>Задачи для занятия</h4>
            <p className="text-secondary" style={{ fontSize: '0.85rem', marginBottom: '0.5rem' }}>
              Попадают в список урока ученика — оттуда он открывает задачи, и можно подключиться.
            </p>
            {lessonAgenda.length === 0 ? (
              <p className="text-secondary">Пока пусто. Вкладка «Подготовка» → найти задачу → «В занятие».</p>
            ) : (
              <ul className="agenda-list agenda-list-editable">
                {lessonAgenda.map((item, idx) => (
                  <li key={`${item.task_id}-${idx}`}>
                    {item.slug ? (
                      <Link to={`/work/${item.slug}`} className="link-like">
                        {item.title || `Задача #${item.task_id}`}
                      </Link>
                    ) : (
                      <span>{item.title || `Задача #${item.task_id}`}</span>
                    )}
                    <button
                      type="button"
                      className="btn btn-xs btn-outline agenda-remove"
                      title="Убрать из занятия"
                      disabled={agendaSaving}
                      onClick={() => removeFromAgenda('lesson', item.task_id)}
                    >
                      ✕
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="agenda-block">
            <h4>ДЗ (черновик)</h4>
            {homeworkDraft.length === 0 ? (
              <p className="text-secondary">Пока пусто. Вкладка «Подготовка» → найти задачу → «В ДЗ».</p>
            ) : (
              <>
                <ul className="agenda-list agenda-list-editable">
                  {homeworkDraft.map((item, idx) => (
                    <li key={`${item.task_id}-${idx}`}>
                      {item.slug ? (
                        <Link to={`/work/${item.slug}`} className="link-like">
                          {item.title || `Задача #${item.task_id}`}
                        </Link>
                      ) : (
                        <span>{item.title || `Задача #${item.task_id}`}</span>
                      )}
                      <button
                        type="button"
                        className="btn btn-xs btn-outline agenda-remove"
                        title="Убрать из ДЗ"
                        disabled={agendaSaving}
                        onClick={() => removeFromAgenda('homework', item.task_id)}
                      >
                        ✕
                      </button>
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  style={{ marginTop: '0.5rem' }}
                  disabled={issueSaving}
                  onClick={() => openIssueDialog(activeSession.id)}
                >
                  Выдать ДЗ
                </button>
              </>
            )}
          </div>
          {!activeSession && leftoverSession && leftoverDraft.length > 0 && (
            <div className="agenda-block">
              <h4>Черновик с урока #{leftoverSession.id}</h4>
              <p className="text-secondary" style={{ marginBottom: '0.5rem' }}>
                Не выдано при завершении. Можно выдать сейчас или очистить.
              </p>
              <ul className="agenda-list agenda-list-editable">
                {leftoverDraft.map((item, idx) => (
                  <li key={`leftover-${item.task_id}-${idx}`}>
                    {item.slug ? (
                      <Link to={`/work/${item.slug}`} className="link-like">
                        {item.title || `Задача #${item.task_id}`}
                      </Link>
                    ) : (
                      <span>{item.title || `Задача #${item.task_id}`}</span>
                    )}
                  </li>
                ))}
              </ul>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.5rem' }}>
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  disabled={issueSaving}
                  onClick={() => openIssueDialog(leftoverSession.id)}
                >
                  Выдать ДЗ
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline"
                  disabled={agendaSaving}
                  onClick={() => void clearLeftoverDraft()}
                >
                  Очистить
                </button>
              </div>
            </div>
          )}
          <div className="agenda-block topic-plan-block">
            <h4>
              <button type="button" className="link-like heading-toggle" onClick={() => { setTopicPlanOpen((o) => !o); if (!topicPlanOpen && topicPlan.length === 0) loadTopicPlan(); }}>
                План по темам {topicPlanOpen ? '▼' : '▶'}
              </button>
            </h4>
            {topicPlanOpen && (
              topicPlanLoading ? (
                <p className="text-secondary">Загрузка...</p>
              ) : topicPlan.length === 0 ? (
                <p className="text-secondary">Пока нет записей. Добавить темы можно через админку или позже из интерфейса.</p>
              ) : (
                <ul className="topic-plan-list">
                  {topicPlan.map((p) => (
                    <li key={p.id}>
                      <span className="topic-name">{p.tag_name || p.tag_slug}</span>
                      <span className={`topic-status status-${p.status}`}>{p.status === 'studied' ? 'Изучено' : p.status === 'planned' ? 'Планируется' : 'Заморожено'}</span>
                      {p.note && <span className="text-secondary">— {p.note}</span>}
                    </li>
                  ))}
                </ul>
              )
            )}
          </div>
        </aside>
      </div>

      <Modal
        open={issueDialogOpen}
        title={issueAndEnd ? 'Выдать ДЗ и завершить урок' : 'Выдать ДЗ'}
        onClose={() => {
          if (issueSaving) return;
          setIssueDialogOpen(false);
          setIssueSessionId(null);
          setIssueAndEnd(false);
        }}
      >
        <label className="form-label">
          Название
          <input
            type="text"
            className="form-input"
            value={issueTitle}
            onChange={(e) => setIssueTitle(e.target.value)}
          />
        </label>
        <label className="form-label" style={{ display: 'block', marginTop: '0.75rem' }}>
          Дедлайн (необязательно)
          <input
            type="date"
            className="form-input"
            value={issueDeadline}
            onChange={(e) => setIssueDeadline(e.target.value)}
          />
        </label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '1rem' }}>
          <button
            type="button"
            className="btn btn-primary"
            disabled={issueSaving}
            onClick={() => void submitIssueHomework()}
          >
            {issueSaving ? '…' : (issueAndEnd ? 'Выдать и завершить' : 'Выдать')}
          </button>
          <button
            type="button"
            className="btn btn-outline"
            disabled={issueSaving}
            onClick={() => {
              setIssueDialogOpen(false);
              setIssueSessionId(null);
              setIssueAndEnd(false);
            }}
          >
            Отмена
          </button>
        </div>
      </Modal>

      <Modal
        open={endDialogOpen && Boolean(activeSession) && !issueDialogOpen}
        title="В черновике есть задачи"
        onClose={() => {
          if (endingLesson || issueSaving) return;
          setEndDialogOpen(false);
        }}
      >
        <p className="text-secondary">
          Выдать их как ДЗ сейчас или завершить урок без выдачи (черновик останется на карточке)?
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '1rem' }}>
          <button
            type="button"
            className="btn btn-primary"
            disabled={issueSaving || endingLesson}
            onClick={() => openIssueDialog(activeSession.id, { andEnd: true })}
          >
            Выдать и завершить…
          </button>
          <button
            type="button"
            className="btn btn-outline"
            disabled={issueSaving || endingLesson}
            onClick={() => void endLessonWithoutIssue()}
          >
            {endingLesson ? '…' : 'Завершить без выдачи'}
          </button>
          <button
            type="button"
            className="btn btn-outline"
            disabled={issueSaving || endingLesson}
            onClick={() => setEndDialogOpen(false)}
          >
            Отмена
          </button>
        </div>
      </Modal>
    </div>
  );
}
